import { useState } from 'react';
import { usePositions, useSession, useSystemStatus } from './api/queries.js';
import { ChartPanel } from './features/chart/ChartPanel.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { LoginScreen } from './features/auth/LoginScreen.js';
import { SessionMenu } from './features/auth/SessionMenu.js';
import { AddPositionDialog } from './features/portfolio/AddPositionDialog.js';
import { useSelectedPosition } from './features/layout/useSelectedPosition.js';
import { useSidebar } from './features/layout/useSidebar.js';
import { PortfolioList } from './features/portfolio/PortfolioList.js';
import { PortfolioSummary } from './features/portfolio/PortfolioSummary.js';

/**
 * Application shell.
 *
 * Two columns: the portfolio on the left, the chart of the selected value on the
 * right. The selection is the only piece of cross-cutting UI state — everything
 * else is server state owned by React Query.
 *
 * Kurse lassen sich von hier aus bewusst **nicht** abrufen. Wer während der
 * Handelszeit abgleicht, speichert eine unfertige Tageskerze als fertigen
 * Schlusskurs — und weil der nächste Lauf erst am Folgetag ansetzt, bliebe sie
 * dauerhaft falsch. Der Abgleich gehört deshalb allein dem Nachtlauf, der nach
 * Börsenschluss läuft. Ein neu angelegter Wert holt seine Historie einmalig beim
 * Anlegen.
 */
export const App = (): React.JSX.Element => {
  const session = useSession();
  const positionsQuery = usePositions({ enabled: session.data != null });
  const systemStatus = useSystemStatus();
  const [addOpen, setAddOpen] = useState(false);
  const sidebar = useSidebar();

  const positions = positionsQuery.data ?? [];

  /*
   * Die Auswahl steht in der Adresszeile — `?wert=<Positions-ID>`.
   *
   * Damit kann die tägliche Auswertung aus der E-Mail heraus auf einen
   * bestimmten Wert zeigen, statt den Empfänger auf der Startseite suchen zu
   * lassen. Der Haken enthält auch die Vorauswahl des ersten Eintrags und das
   * Nachführen beim Löschen.
   */
  const { selectedId, select: setSelectedId } = useSelectedPosition(positions);

  const selected = positions.find((position) => position.id === selectedId) ?? null;
  const triggeredCount = positions.filter((p) => p.stopLossEvaluation.triggered).length;

  /*
   * Die Anmeldung entscheidet vor allem anderen. Erst danach werden Daten geladen
   * — sonst liefe gegen einen ausgesperrten Server eine Reihe von Abfragen, die
   * sämtlich mit 401 enden, und der Nutzer sähe Fehlermeldungen statt der Frage,
   * die tatsächlich ansteht: Wer bist du?
   */
  if (session.isPending) {
    return <p className="app__loading muted">Lädt …</p>;
  }

  if (session.isError) {
    return (
      <p className="hint hint--error app__banner">
        Die API ist nicht erreichbar. Läuft sie auf Port 3001?
      </p>
    );
  }

  if (!session.data) {
    return <LoginScreen />;
  }

  return (
    <div className="app">
      <header className="app__header">
        {/*
          Der Umschalter sitzt in der Kopfzeile und nicht an der Leiste selbst:
          Zugeklappt wäre er dort nicht mehr da, und man suchte den Weg zurück.
        */}
        <button
          type="button"
          className="app__sidebar-toggle"
          onClick={sidebar.umschalten}
          aria-expanded={!sidebar.eingeklappt}
          aria-controls="portfolio-leiste"
          title={sidebar.eingeklappt ? 'Portfolio einblenden' : 'Portfolio ausblenden'}
        >
          <span className="visually-hidden">
            {sidebar.eingeklappt ? 'Portfolio einblenden' : 'Portfolio ausblenden'}
          </span>
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <rect
              x="1.5"
              y="2.5"
              width="13"
              height="11"
              rx="1.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <rect
              x="1.5"
              y="2.5"
              width="4.5"
              height="11"
              fill="currentColor"
              opacity={sidebar.eingeklappt ? 0.25 : 1}
            />
          </svg>
        </button>

        <h1>
          StockStop
          <span className="muted"> · Stop-Loss Verwaltung</span>
        </h1>

        <div className="app__header-actions">
          {triggeredCount > 0 ? (
            <span className="badge badge--danger">
              {triggeredCount} {triggeredCount === 1 ? 'Wert' : 'Werte'} am Stop
            </span>
          ) : null}
          <SessionMenu user={session.data} />
        </div>
      </header>

      {systemStatus.data?.provider.isMock ? (
        <p className="hint hint--warn app__banner">
          <strong>Testdaten aktiv.</strong> Die angezeigten Kurse sind generiert und entsprechen
          nicht dem Markt. Für echte Kurse in der <code>.env</code>{' '}
          <code>MARKET_DATA_PROVIDER=twelvedata</code> setzen, einen API-Key hinterlegen,
          anschließend <code>npm run db:reset-prices -- --yes</code> und{' '}
          <code>npm run backfill</code> ausführen.
        </p>
      ) : null}

      <main className={`app__body${sidebar.eingeklappt ? ' app__body--schmal' : ''}`}>
        <aside className="panel portfolio-panel" id="portfolio-leiste" hidden={sidebar.eingeklappt}>
          <div className="portfolio-panel__head">
            <h2>Portfolio</h2>
            <button
              type="button"
              className="portfolio-panel__add"
              onClick={() => setAddOpen(true)}
              title="Wert hinzufügen"
            >
              <span className="visually-hidden">Wert hinzufügen</span>
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                <path
                  d="M7 1.5v11M1.5 7h11"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/*
            Stehen bleibt allein die Überschrift. Die Summen rollen mit: Sie sind
            die Antwort auf den ersten Blick, nicht auf den fünfzigsten — und
            dauerhaft festgehalten nähmen sie den Kacheln Fläche weg, für die man
            eigentlich rollt.
          */}
          <div className="portfolio-panel__scroll">
            <PortfolioSummary
              positions={positions}
              currency={positions[0]?.instrument.currency ?? null}
            />

            {positionsQuery.isPending ? <p className="muted">Lädt …</p> : null}
            {positionsQuery.isError ? (
              <p className="hint hint--error">
                Portfolio konnte nicht geladen werden. Läuft die API auf Port 3001?
              </p>
            ) : null}

            {positionsQuery.isSuccess ? (
              <ErrorBoundary area="Das Portfolio">
                <PortfolioList
                  positions={positions}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                />
              </ErrorBoundary>
            ) : null}
          </div>
        </aside>

        <ErrorBoundary area="Der Chart">
          <ChartPanel position={selected} />
        </ErrorBoundary>
      </main>

      <AddPositionDialog isOpen={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
};
